import React, { useState, useMemo } from 'react';
import { useTheme } from '@/theme';
import { Text } from '@/components/ui';
import { Button } from '@/components/ui';
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '@/components/common/DataTable';
import {
  getCalibrationLogsByDevice as getLiteCalibrationLogs,
  type LiteCalibrationLog,
} from '@/api/device/logs/liteLog';
import {
  getCalibrationLogsByDevice as getMiniCalibrationLogs,
  type MiniCalibrationLog,
} from '@/api/device/logs/miniLog';
import { generateCalibrationCertificate } from '@/utils/calibrationCertificate';
import { getConfigurationAtTimestamp } from '@/api/device/apiUnified';
import { MINI_SENSOR_TYPE_INFO, MiniSensorType } from '@packages/types/device/mini/sensors';
import { formatDateTime } from '@/utils/date';
import { useTranslation } from 'react-i18next';
import { i18nKeys } from '@/i18n/i18nKeyGen';
import { useModalStore } from '@/store/modalStore';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import {
  TabLayout,
  TabHeader,
  ErrorMessage,
  EmptyState,
  LoadingSkeleton,
  DownloadButtons,
} from '@/components/layout/TabLayout';
import { useLogTabState, useLogFetch } from './hooks/useLogTabState';
import * as XLSX from 'xlsx';
import { generatePdf } from '@/utils/pdfGenerator';

interface Device {
  serial_number: string;
  device_type?: string;
  calibration_due_date?: string;
  calibration_interval?: number[];
  bump_test_due_date?: string;
  bump_interval?: number[];
  sensor_types?: string[];
}

interface CalibrationBumpTabProps {
  device: Device;
  operator?: string;
  onDownloadPdf?: () => Promise<void>;
}

export const CalibrationBumpTab: React.FC<CalibrationBumpTabProps> = ({
  device,
  operator = 'bellcloud',
  onDownloadPdf: _onDownloadPdf,
}) => {
  const { theme } = useTheme();
  const { t } = useTranslation('datalog');
  const { showAlert } = useModalStore();

  const {
    loading, setLoading,
    error, setError, handleError,
    dateRange, setDateRange,
    isLite, isMini,
    shouldFetch,
  } = useLogTabState({ device });

  const [logs, setLogs] = useState<(LiteCalibrationLog | MiniCalibrationLog)[]>([]);
  const sensorChartRef = React.useRef<any>(null);
  const testTypeChartRef = React.useRef<any>(null);

  const fetchCalibrationLogs = async () => {
    if (!device.serial_number || !dateRange.from || !dateRange.to) {
      setLogs([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (isLite) {
        const data = await getLiteCalibrationLogs(
          device.serial_number,
          dateRange.from,
          dateRange.to,
          10000
        );
        setLogs(data);
      } else if (isMini) {
        const data = await getMiniCalibrationLogs(
          device.serial_number,
          dateRange.from,
          dateRange.to,
          10000
        );
        setLogs(data);
      }
    } catch (err: unknown) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  useLogFetch({
    shouldFetch,
    deps: [device.serial_number, device.device_type, dateRange.from, dateRange.to],
    fetchFn: fetchCalibrationLogs,
  });

  // log_type 텍스트는 백엔드에서 제공하는 log_type_text를 사용
  // 프론트엔드에서 재매핑하지 않음 (중복 파싱 방지)

  const ResultIndicator: React.FC<{ result_text: string }> = ({ result_text }) => {
    const isPass = result_text === 'PASS' || result_text === 'Success';
    const displayText =
      result_text === 'Success' ? 'PASS' : result_text === 'Fail' ? 'FAIL' : result_text;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing[2] }}>
        <div
          style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: isPass
              ? theme.colors.status.stable.fill.surface
              : theme.colors.status.danger.fill.surface,
            flexShrink: 0,
          }}
        />
        <span
          style={{
            color: isPass
              ? theme.colors.status.stable.stroke.text
              : theme.colors.status.danger.stroke.text,
            fontWeight: theme.fontWeight.medium,
            fontSize: `${theme.fontSize.sm}px`,
          }}
        >
          {displayText}
        </span>
      </div>
    );
  };

  // formatDateTime은 자동으로 사용자 timezone으로 변환
  // UTC timestamp -> 사용자 로컬 시간 (YYYY-MM-DD HH:mm:ss)

  // 테이블 데이터 그룹화 (같은 timestamp의 센서들을 하나의 행으로)
  interface SensorData {
    sensor_name: string;
    gas_conc: number | null;
    conc_unit_text?: string;
    result: number;
    result_text: string;
  }

  interface GroupedCalibrationLog {
    timestamp: string;
    log_type: number;
    log_type_text: string;
    sensors: SensorData[];
    temperature: number | null;
    battery_voltage: number | null;
    is_mini: boolean;
  }

  const groupedLogs = useMemo<GroupedCalibrationLog[]>(() => {
    const groupMap = new Map<string, GroupedCalibrationLog>();

    logs.forEach(log => {
      const key = log.timestamp;

      if ('sensors' in log) {
        // LITE: 센서 배열을 그룹화
        if (!groupMap.has(key)) {
          groupMap.set(key, {
            timestamp: log.timestamp,
            log_type: log.sensors[0]?.log_type || 0,
            log_type_text: log.sensors[0]?.log_type_text || 'Unknown',
            sensors: [],
            temperature: log.sensors[0]?.temperature || null,
            battery_voltage: log.sensors[0]?.battery_voltage || null,
            is_mini: false,
          });
        }

        const group = groupMap.get(key)!;
        log.sensors.forEach((sensor: SensorData) => {
          const exists = group.sensors.some(s => s.sensor_name === sensor.sensor_name);
          if (!exists) {
            group.sensors.push({
              sensor_name: sensor.sensor_name,
              gas_conc: sensor.gas_conc,
              conc_unit_text: sensor.conc_unit_text,
              result: sensor.result,
              result_text: sensor.result_text,
            });
          }
        });
      } else {
        // MINI: 단일 센서
        if (!groupMap.has(key)) {
          groupMap.set(key, {
            timestamp: log.timestamp,
            log_type: log.log_type,
            log_type_text: log.log_type_text,
            sensors: [],
            temperature: log.temperature,
            battery_voltage: log.battery_voltage,
            is_mini: true,
          });
        }

        const group = groupMap.get(key)!;
        const exists = group.sensors.some(s => s.sensor_name === log.sensor_name);
        if (!exists) {
          group.sensors.push({
            sensor_name: log.sensor_name,
            gas_conc: log.gas_conc,
            conc_unit_text: 'ppm',
            result: log.result,
            result_text: log.result_text,
          });
        }
      }
    });

    return Array.from(groupMap.values()).sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
  }, [logs]);

  // Certificate 발행 핸들러
  const handleGenerateCertificate = async (log: GroupedCalibrationLog) => {
    try {
      // Device configuration 가져오기 (Set Point 정보 포함)
      // ⚠️ 중요: calibration timestamp 이전의 가장 최신 configuration을 가져와야 함
      let setPointData: Record<
        string,
        {
          low_alarm?: number;
          high_alarm?: number;
          twa_alarm?: number;
          stel_alarm?: number;
          conc_unit_text?: string;
          result_text?: string;
        }
      > = {};

      // Due Date는 이미 tab에 표시된 device 정보에서 가져오기
      let calibrationDueDate = 'N/A';
      if (log.log_type === 3) {
        // Bump Test Due Date
        if (device.bump_test_due_date) {
          const dueDate = new Date(device.bump_test_due_date);
          calibrationDueDate = dueDate.toISOString().split('T')[0]; // YYYY-MM-DD 형식
        }
      } else {
        // Calibration Due Date
        if (device.calibration_due_date) {
          const dueDate = new Date(device.calibration_due_date);
          calibrationDueDate = dueDate.toISOString().split('T')[0]; // YYYY-MM-DD 형식
        }
      }

      try {
        // 📅 calibration timestamp 기준으로 configuration 조회
        // ⚠️ LITE: lite_sensor_config_log 테이블 사용
        // ⚠️ MINI: mini_configuration_log 테이블 사용
        const configData = await getConfigurationAtTimestamp({
          serial_number: device.serial_number,
          timestamp: log.timestamp,
          is_mini: log.is_mini,
        });

        if (configData && configData.sensors) {
          const sensorNames = log.sensors.map(s => s.sensor_name);

          if (log.is_mini) {
            // MINI: mini_configuration_log의 센서별 레코드 배열
            // sensor_num으로 센서를 식별하고, MINI_SENSOR_TYPE_INFO를 사용하여 센서 이름으로 매핑
            sensorNames.forEach(sensorName => {
              // MINI_SENSOR_TYPE_INFO에서 센서 이름으로 sensor_num 찾기
              const sensorTypeEntry = Object.entries(MINI_SENSOR_TYPE_INFO).find(
                ([_, info]) => info.label === sensorName
              );

              if (sensorTypeEntry) {
                const sensorType = parseInt(sensorTypeEntry[0]) as MiniSensorType;
                const sensorInfo = MINI_SENSOR_TYPE_INFO[sensorType];
                const sensorNum = sensorInfo.index;

                // 해당 sensor_num의 설정 찾기
                const sensorConfig = configData.sensors.find(
                  (s: any) => s.sensor_num === sensorNum
                );

                if (sensorConfig) {
                  const lowAlarm = sensorConfig.low_alarm_threshold
                    ? Number(sensorConfig.low_alarm_threshold)
                    : null;
                  const highAlarm = sensorConfig.high_alarm_threshold
                    ? Number(sensorConfig.high_alarm_threshold)
                    : null;
                  const twaAlarm =
                    sensorConfig.twa_threshold && Number(sensorConfig.twa_threshold) !== 0
                      ? Number(sensorConfig.twa_threshold)
                      : null;
                  const stelAlarm =
                    sensorConfig.stel_threshold && Number(sensorConfig.stel_threshold) !== 0
                      ? Number(sensorConfig.stel_threshold)
                      : null;

                  setPointData[sensorName] = {
                    low_alarm: lowAlarm ?? undefined,
                    high_alarm: highAlarm ?? undefined,
                    twa_alarm: twaAlarm ?? undefined,
                    stel_alarm: stelAlarm ?? undefined,
                    conc_unit_text: 'ppm', // MINI는 항상 ppm
                  };
                } else {
                  console.warn(
                    `[CalibrationBumpTab] No sensor config found for sensor_num ${sensorNum} (${sensorName})`
                  );
                }
              } else {
                console.warn(
                  `[CalibrationBumpTab] No sensor type info found for sensor name: ${sensorName}`
                );
              }
            });
          } else {
            // LITE: lite_sensor_config_log의 센서별 레코드 배열
            // sensor_type_text로 센서를 식별
            sensorNames.forEach(sensorName => {
              // 센서 이름으로 설정 찾기
              const sensorConfig = configData.sensors.find(
                (s: any) => s.sensor_type_text === sensorName
              );

              if (sensorConfig) {
                const lowAlarm = sensorConfig.low_alarm_threshold
                  ? Number(sensorConfig.low_alarm_threshold)
                  : null;
                const highAlarm = sensorConfig.high_alarm_threshold
                  ? Number(sensorConfig.high_alarm_threshold)
                  : null;
                const twaAlarm =
                  sensorConfig.twa_threshold && Number(sensorConfig.twa_threshold) !== 0
                    ? Number(sensorConfig.twa_threshold)
                    : null;
                const stelAlarm =
                  sensorConfig.stel_threshold && Number(sensorConfig.stel_threshold) !== 0
                    ? Number(sensorConfig.stel_threshold)
                    : null;

                setPointData[sensorName] = {
                  low_alarm: lowAlarm ?? undefined,
                  high_alarm: highAlarm ?? undefined,
                  twa_alarm: twaAlarm ?? undefined,
                  stel_alarm: stelAlarm ?? undefined,
                  conc_unit_text:
                    sensorConfig.conc_unit_text ||
                    log.sensors.find(s => s.sensor_name === sensorName)?.conc_unit_text ||
                    'ppm',
                };
              }
            });
          }
        }
      } catch (err) {
        console.warn('[CalibrationBumpTab] Failed to fetch configuration at timestamp:', err);
        // Set Point 정보를 가져오지 못해도 Certificate는 발행 가능
      }

      // 센서 데이터에 Set Point 정보 추가
      const sensorsWithSetPoint = log.sensors.map(sensor => ({
        ...sensor,
        ...setPointData[sensor.sensor_name],
      }));

      generateCalibrationCertificate({
        serialNumber: device.serial_number,
        detectorName: `BELLILITE_${device.serial_number}`,
        calibrationDueDate: calibrationDueDate,
        operator: operator || 'N/A',
        timestamp: log.timestamp,
        logType: log.log_type,
        sensors: sensorsWithSetPoint,
        temperature: log.temperature,
      });
    } catch (err) {
      console.error('[CalibrationBumpTab] Failed to generate certificate:', err);
      showAlert(t(i18nKeys.datalog.certificateGenerationFailed), '', undefined, 'danger');
    }
  };

  // DataTable 컬럼 정의 (그룹화된 데이터용)
  const columns = useMemo<ColumnDef<GroupedCalibrationLog>[]>(() => {
    return [
      {
        accessorKey: 'timestamp',
        header: 'Timestamp',
        cell: ({ getValue }) => (
          <div style={{ whiteSpace: 'pre-line' }}>{formatDateTime(getValue() as string)}</div>
        ),
        size: 160,
        meta: {
          style: { textAlign: 'center' },
        },
      },
      {
        accessorKey: 'log_type_text',
        header: 'Type',
        size: 120,
        meta: {
          style: { textAlign: 'center' },
        },
      },
      {
        id: 'sensor',
        header: 'Sensor',
        cell: ({ row }) => {
          const log = row.original;
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing[2] }}>
              {log.sensors.map((sensor, idx) => (
                <div key={idx}>{sensor.sensor_name}</div>
              ))}
            </div>
          );
        },
        size: 100,
      },
      {
        id: 'gas_conc',
        header: 'Gas Conc.',
        cell: ({ row }) => {
          const log = row.original;
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing[2] }}>
              {log.sensors.map((sensor, idx) => (
                <div key={idx}>
                  {sensor.gas_conc !== null
                    ? `${sensor.gas_conc} ${sensor.conc_unit_text || 'ppm'}`
                    : '-'}
                </div>
              ))}
            </div>
          );
        },
        size: 120,
      },
      {
        id: 'result',
        header: 'Result',
        cell: ({ row }) => {
          const log = row.original;
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing[2] }}>
              {log.sensors.map((sensor, idx) => (
                <div key={idx}>
                  <ResultIndicator result_text={sensor.result_text} />
                </div>
              ))}
            </div>
          );
        },
        size: 200,
      },
      {
        accessorKey: 'temperature',
        header: 'Temp.',
        cell: ({ getValue }) => {
          const temp = getValue() as number | null;
          return temp !== null ? `${temp}°C` : '-';
        },
        size: 100,
        meta: {
          style: { textAlign: 'center' },
        },
      },
      {
        accessorKey: 'battery_voltage',
        header: 'Battery',
        cell: ({ getValue }) => {
          const battery = getValue() as number | null;
          return battery !== null ? `${battery}V` : '-';
        },
        size: 100,
        meta: {
          style: { textAlign: 'center' },
        },
      },
      {
        id: 'actions',
        header: 'Certificate',
        cell: ({ row }) => {
          const log = row.original;
          // Fresh Air Calibration은 Certificate 발행 불가
          if (log.log_type_text === 'Fresh Air Calibration') {
            return <span style={{ color: theme.colors.text.secondary }}>-</span>;
          }
          // log_type === 1이고 CO2 센서가 없으면 Fresh Air Calibration
          if (log.log_type === 1) {
            const hasCO2 = log.sensors.some(sensor => sensor.sensor_name === 'CO2');
            if (!hasCO2) {
              return <span style={{ color: theme.colors.text.secondary }}>-</span>;
            }
          }
          return (
            <Button variant="secondary" onClick={() => handleGenerateCertificate(log)} width="80px">
              Download
            </Button>
          );
        },
        size: 80,
        meta: {
          style: { textAlign: 'center' },
        },
      },
    ];
  }, [theme, device]);

  // 센서별 Pass/Fail 요약 데이터 계산
  const sensorSummaryData = useMemo(() => {
    const sensorMap = new Map<string, { pass: number; fail: number }>();

    groupedLogs.forEach(log => {
      log.sensors.forEach(sensor => {
        const existing = sensorMap.get(sensor.sensor_name) || { pass: 0, fail: 0 };
        if (sensor.result === 1) {
          existing.pass += 1;
        } else {
          existing.fail += 1;
        }
        sensorMap.set(sensor.sensor_name, existing);
      });
    });

    return Array.from(sensorMap.entries())
      .map(([sensor_name, counts]) => ({
        sensor_name,
        pass: counts.pass,
        fail: counts.fail,
        total: counts.pass + counts.fail,
      }))
      .sort((a, b) => a.sensor_name.localeCompare(b.sensor_name));
  }, [groupedLogs]);

  // Test Type별 Pass/Fail 요약 데이터 계산
  const testTypeSummaryData = useMemo(() => {
    const testTypeMap = new Map<string, { pass: number; fail: number }>();

    groupedLogs.forEach(log => {
      const testType = log.log_type_text;
      const existing = testTypeMap.get(testType) || { pass: 0, fail: 0 };

      log.sensors.forEach(sensor => {
        if (sensor.result === 1) {
          existing.pass += 1;
        } else {
          existing.fail += 1;
        }
      });

      testTypeMap.set(testType, existing);
    });

    return Array.from(testTypeMap.entries())
      .map(([test_type, counts]) => ({
        test_type,
        pass: counts.pass,
        fail: counts.fail,
        total: counts.pass + counts.fail,
      }))
      .sort((a, b) => a.test_type.localeCompare(b.test_type));
  }, [groupedLogs]);

  // 두 차트의 최대값 계산 (Y축 범위 통일)
  const maxYValue = useMemo(() => {
    const sensorMax = Math.max(...sensorSummaryData.map(item => item.total), 0);
    const testTypeMax = Math.max(...testTypeSummaryData.map(item => item.total), 0);
    return Math.max(sensorMax, testTypeMax);
  }, [sensorSummaryData, testTypeSummaryData]);

  // 센서별 Pass/Fail 차트 옵션
  const sensorChartOption = useMemo<EChartsOption>(() => {
    return {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
        },
      },
      legend: {
        data: ['Pass', 'Fail'],
        top: '0',
        right: '10',
        textStyle: { color: theme.colors.text.primary },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        top: '15%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: sensorSummaryData.map(item => item.sensor_name),
        axisLabel: { color: theme.colors.text.primary, fontSize: 11 },
        axisLine: { lineStyle: { color: theme.colors.border.container } },
        axisTick: { lineStyle: { color: theme.colors.border.container } },
        splitLine: { show: false },
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: maxYValue,
        minInterval: 1,
        axisLabel: { color: theme.colors.text.primary, fontSize: 11 },
        axisLine: { show: true, lineStyle: { color: theme.colors.border.container } },
        splitLine: {
          lineStyle: { color: theme.colors.border.divider, type: 'dashed' as const },
        },
      },
      series: [
        {
          name: 'Pass',
          type: 'bar',
          stack: 'total',
          itemStyle: {
            color: theme.colors.status.stable.fill.surface,
          },
          label: {
            show: true,
            position: 'inside',
            formatter: (params: any) => (params.value > 0 ? params.value : ''),
            color: '#fff',
            fontWeight: 'bold',
          },
          data: sensorSummaryData.map(item => item.pass),
        },
        {
          name: 'Fail',
          type: 'bar',
          stack: 'total',
          itemStyle: {
            color: theme.colors.status.danger.fill.surface,
          },
          label: {
            show: true,
            position: 'inside',
            formatter: (params: any) => (params.value > 0 ? params.value : ''),
            color: '#fff',
            fontWeight: 'bold',
          },
          data: sensorSummaryData.map(item => item.fail),
        },
      ],
    };
  }, [sensorSummaryData, theme, maxYValue]);

  // Test Type별 Pass/Fail 차트 옵션
  const testTypeChartOption = useMemo<EChartsOption>(() => {
    return {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
        },
      },
      legend: {
        data: ['Pass', 'Fail'],
        top: '0',
        right: '10',
        textStyle: { color: theme.colors.text.primary },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        top: '15%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: testTypeSummaryData.map(item => item.test_type),
        axisLabel: {
          interval: 0,
          color: theme.colors.text.primary,
          fontSize: 11,
        },
        axisLine: { lineStyle: { color: theme.colors.border.container } },
        axisTick: { lineStyle: { color: theme.colors.border.container } },
        splitLine: { show: false },
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: maxYValue,
        minInterval: 1,
        axisLabel: { color: theme.colors.text.primary, fontSize: 11 },
        axisLine: { show: true, lineStyle: { color: theme.colors.border.container } },
        splitLine: {
          lineStyle: { color: theme.colors.border.divider, type: 'dashed' as const },
        },
      },
      series: [
        {
          name: 'Pass',
          type: 'bar',
          stack: 'total',
          itemStyle: {
            color: theme.colors.status.stable.fill.surface,
          },
          label: {
            show: true,
            position: 'inside',
            formatter: (params: any) => (params.value > 0 ? params.value : ''),
            color: '#fff',
            fontWeight: 'bold',
          },
          data: testTypeSummaryData.map(item => item.pass),
        },
        {
          name: 'Fail',
          type: 'bar',
          stack: 'total',
          itemStyle: {
            color: theme.colors.status.danger.fill.surface,
          },
          label: {
            show: true,
            position: 'inside',
            formatter: (params: any) => (params.value > 0 ? params.value : ''),
            color: '#fff',
            fontWeight: 'bold',
          },
          data: testTypeSummaryData.map(item => item.fail),
        },
      ],
    };
  }, [testTypeSummaryData, theme, maxYValue]);

  // 테이블 데이터 변환 함수 (Excel과 PDF에서 공통 사용)
  const convertToTableData = () => {
    const tableData: any[] = [];

    groupedLogs.forEach(log => {
      if (log.sensors.length === 0) {
        tableData.push({
          Timestamp: formatDateTime(log.timestamp),
          Type: log.log_type_text,
          Sensor: '',
          'Gas Conc.': '',
          Result: '',
          'Temp.': log.temperature !== null ? `${log.temperature}°C` : '-',
          Battery: log.battery_voltage !== null ? `${log.battery_voltage}V` : '-',
        });
      } else {
        log.sensors.forEach((sensor, index) => {
          tableData.push({
            Timestamp: index === 0 ? formatDateTime(log.timestamp) : '',
            Type: index === 0 ? log.log_type_text : '',
            Sensor: sensor.sensor_name,
            'Gas Conc.':
              sensor.gas_conc !== null
                ? `${sensor.gas_conc} ${sensor.conc_unit_text || 'ppm'}`
                : '-',
            Result: sensor.result_text,
            'Temp.': index === 0 && log.temperature !== null ? `${log.temperature}°C` : '',
            Battery: index === 0 && log.battery_voltage !== null ? `${log.battery_voltage}V` : '',
          });
        });
      }
    });

    return tableData;
  };

  // Excel 다운로드 핸들러
  const handleExcelDownload = () => {
    const excelData = convertToTableData();

    // 워크시트 생성
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // 워크북 생성
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Calibration & Bump Test');

    // 파일 다운로드
    const fileName = `calibration_bump_${device.serial_number}_${dateRange.from}_${dateRange.to}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  // PDF 다운로드 핸들러
  const handleDownloadPdf = async () => {
    try {
      const tableData = convertToTableData();
      const tableColumns = [
        { header: 'Timestamp', accessorKey: 'Timestamp' },
        { header: 'Type', accessorKey: 'Type' },
        { header: 'Sensor', accessorKey: 'Sensor' },
        { header: 'Gas Conc.', accessorKey: 'Gas Conc.' },
        { header: 'Result', accessorKey: 'Result' },
        { header: 'Temp.', accessorKey: 'Temp.' },
        { header: 'Battery', accessorKey: 'Battery' },
      ];

      // 두 개의 차트 이미지 캡처 및 합치기
      let chartImageUrl: string | undefined;

      const sensorChartImage = sensorChartRef.current
        ? sensorChartRef.current.getEchartsInstance()?.getDataURL({
            type: 'png',
            pixelRatio: 2,
            backgroundColor: '#fff',
          })
        : null;

      const testTypeChartImage = testTypeChartRef.current
        ? testTypeChartRef.current.getEchartsInstance()?.getDataURL({
            type: 'png',
            pixelRatio: 2,
            backgroundColor: '#fff',
          })
        : null;

      // 두 차트를 하나의 이미지로 합치기
      if (sensorChartImage || testTypeChartImage) {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (ctx) {
          // 캔버스 크기 설정 (두 차트를 좌우로 배치)
          const chartWidth = 800;
          const chartHeight = 400;
          const gap = 40;

          canvas.width = sensorChartImage && testTypeChartImage ? chartWidth * 2 + gap : chartWidth;
          canvas.height = chartHeight;

          // 배경색 설정
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, canvas.width, canvas.height);

          // 첫 번째 차트 그리기
          if (sensorChartImage) {
            const img1 = new Image();
            img1.src = sensorChartImage;
            await new Promise(resolve => {
              img1.onload = () => {
                ctx.drawImage(img1, 0, 0, chartWidth, chartHeight);
                resolve(null);
              };
            });
          }

          // 두 번째 차트 그리기
          if (testTypeChartImage) {
            const img2 = new Image();
            img2.src = testTypeChartImage;
            await new Promise(resolve => {
              img2.onload = () => {
                const xOffset = sensorChartImage ? chartWidth + gap : 0;
                ctx.drawImage(img2, xOffset, 0, chartWidth, chartHeight);
                resolve(null);
              };
            });
          }

          chartImageUrl = canvas.toDataURL('image/png');
        }
      }

      await generatePdf({
        logType: 'Calibration & Bump',
        serialNumber: device.serial_number,
        chartImageUrl,
        tableData,
        tableColumns,
      });
    } catch (error) {
      console.error('Failed to generate PDF:', error);
    }
  };

  return (
    <TabLayout>
      {/* 헤더 */}
      <TabHeader dateRange={dateRange} onDateRangeChange={setDateRange} />

      {/* 에러 메시지 */}
      {error && !loading && <ErrorMessage message={error} />}

      {loading ? (
        <LoadingSkeleton count={12} showChart={true} />
      ) : logs.length === 0 ? (
        <EmptyState message={t(i18nKeys.datalog.noDataInPeriod)} />
      ) : (
        <div
          style={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing[4],
            width: '100%',
          }}
        >
          <div
            style={{
              flex: 1,
              minHeight: 0,
              width: '100%',
              backgroundColor: theme.colors.surface.layout.overlay,
              borderRadius: theme.borderRadius.md,
              padding: theme.spacing[6],
              position: 'relative',
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                overflow: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: theme.spacing[5],
              }}
            >
              {/* Pass/Fail 요약 차트 (좌우 배치) */}
              {(sensorSummaryData.length > 0 || testTypeSummaryData.length > 0) && (
                <div
                  style={{
                    width: '100%',
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: theme.spacing[6],
                    flexShrink: 0,
                  }}
                >
                  {/* 센서별 Pass/Fail 요약 차트 */}
                  {sensorSummaryData.length > 0 && (
                    <div
                      style={{
                        width: '100%',
                        borderRadius: theme.borderRadius.md,
                        border: `1px solid ${theme.colors.border.container}`,
                        padding: theme.spacing[6],
                      }}
                    >
                      <Text style={{ marginBottom: theme.spacing[6] }}>
                        Pass/Fail Summary by Sensor
                      </Text>
                      <ReactECharts
                        ref={sensorChartRef}
                        option={sensorChartOption}
                        style={{ height: '250px' }}
                      />
                    </div>
                  )}

                  {/* Test Type별 Pass/Fail 요약 차트 */}
                  {testTypeSummaryData.length > 0 && (
                    <div
                      style={{
                        width: '100%',
                        backgroundColor: theme.colors.surface.layout.overlay,
                        borderRadius: theme.borderRadius.md,
                        border: `1px solid ${theme.colors.border.container}`,
                        padding: theme.spacing[6],
                      }}
                    >
                      <Text style={{ marginBottom: theme.spacing[6] }}>
                        Pass/Fail Summary by Test Type
                      </Text>
                      <ReactECharts
                        ref={testTypeChartRef}
                        option={testTypeChartOption}
                        style={{ height: '250px' }}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Calibration & Bump Test 이력 */}
              <div
                style={{
                  width: '100%',
                  borderRadius: theme.borderRadius.md,
                  flexShrink: 0,
                }}
              >
                <Text style={{ marginBottom: theme.spacing[6] }}>
                  Calibration & Bump Test History
                </Text>
                <DataTable
                  data={groupedLogs}
                  columns={columns}
                  enablePagination={true}
                  pageSize={20}
                  pageSizeOptions={[10, 20, 50, 100]}
                  enableSorting={true}
                  emptyMessage={t(i18nKeys.datalog.noDataMessage)}
                />
              </div>
            </div>
          </div>
          <DownloadButtons
            onDownloadExcel={handleExcelDownload}
            onDownloadPdf={handleDownloadPdf}
            disabled={loading || groupedLogs.length === 0}
          />
        </div>
      )}
    </TabLayout>
  );
};
